import { buttonBaseClasses } from "@mui/material/ButtonBase";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography, { typographyClasses } from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import SimpleBar from "simplebar-react";

import { DRAWER_WIDTH as RIGHT_DRAWER_WIDTH } from "@/components/Core/Drawer/data";
import { colors } from "@/theme";

// Room for the sportsbook tree's flags, names and two counts side by side.
const drawerWidth = 280;

export const SidebarDrawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  boxSizing: "border-box",
  color: colors.white,
  width: drawerWidth,

  [theme.breakpoints.between("sm", "md")]: {
    width: "61px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    display: "none",
  },
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    [theme.breakpoints.between("sm", "md")]: {
      width: "61px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
}));

/**
 * The heading over a group of menu items. Also the button that folds the group
 * away, when it is given something to do on a press.
 */
export const SidebarSectionLabelRoot = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  width: "100%",
  padding: theme.spacing(1.25, 2, 0.5, 2),
  border: 0,
  background: "none",
  textAlign: "left",
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  color: colors.greyDark,
  "&:is(button)": {
    cursor: "pointer",
    "&:hover": { color: colors.prime },
    "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: -2, borderRadius: 6 },
  },
  "& svg": { width: 16, height: 16, flexShrink: 0, transition: "transform 200ms ease" },
  "&[aria-expanded='false'] svg": { transform: "rotate(-90deg)" },
  "@media (prefers-reduced-motion: reduce)": { "& svg": { transition: "none" } },
})) as typeof Typography;

export const NavList = styled(List, {
  shouldForwardProp: (prop) => prop !== "isTablet",
})<{ isTablet?: boolean }>(({ theme, isTablet }) => ({
  paddingLeft: isTablet ? 0 : theme.spacing(1),
}));

/**
 * One item of the menu.
 *
 * The page the player is on is a pill in the brand blue with a bar down its
 * left edge; the pointer gets a fainter one. It was orange in the light scheme
 * and blue in the dark one, a half-rounded outlined tab in both, and took a
 * full second to answer the pointer - long enough to look like it had not.
 */
export const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: "block",
  paddingRight: theme.spacing(1),
  [`& svg`]: {
    width: "20px",
    height: "20px",
    color: colors.greyDark,
  },
  [`& .nav-icon`]: {
    color: colors.greyDark,
  },
  [`& .${typographyClasses.root}`]: {
    fontWeight: 500,
    color: colors.greyDark,
  },
  [`& .${buttonBaseClasses.root}`]: {
    position: "relative",
    display: "flex",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 10,
    transition: "background-color 160ms ease",
    [`& .${typographyClasses.root}, & svg, & .nav-icon`]: {
      transition: "color 160ms ease",
    },
    // The bar that marks the page the player is on.
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      width: 3,
      height: 18,
      borderRadius: 3,
      backgroundColor: colors.prime,
      transform: "translateY(-50%) scaleY(0)",
      transition: "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    },
    "&:hover": {
      background: alpha(colors.prime, 0.08),
      [`& .${typographyClasses.root}`]: { color: theme.palette.text.primary },
      [`& svg, & .nav-icon`]: { color: colors.prime },
    },
    "&.active": {
      background: alpha(colors.prime, 0.14),
      "&::before": { transform: "translateY(-50%) scaleY(1)" },
      [`& .${typographyClasses.root}`]: { fontWeight: 700, color: colors.prime },
      [`& svg, & .nav-icon`]: { color: colors.prime },
    },
    "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: -2 },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&::before": { transition: "none" },
    },
  },
}));

const HEADER_SIDE_WIDTH_OPEN = RIGHT_DRAWER_WIDTH;

export const HeaderContainer = styled("header", {
  shouldForwardProp: (prop) => prop !== "sideWidth",
})<{ sideWidth: boolean }>(({ theme, sideWidth }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  position: "fixed",
  zIndex: 100,
  transition: "background 0.3s ease-in-out",
  // Frosted in the page's own color. It was clear with a one-pixel blur, so the
  // page scrolling under it showed through the header's controls as noise.
  background: `rgba(${theme.vars.palette.background.defaultChannel} / 0.78)`,
  backdropFilter: "blur(14px) saturate(1.2)",
  WebkitBackdropFilter: "blur(14px) saturate(1.2)",
  borderBottom: `1px solid ${colors.border}`,
  right: 0,
  width: "100%",
  height: "68px",
  boxSizing: "border-box",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),

  [theme.breakpoints.up("sm")]: {
    maxWidth: "100%",
    right: sideWidth ? HEADER_SIDE_WIDTH_OPEN : 0,
    width: sideWidth ? "calc(100% - 361px)" : "calc(100% - 61px)",
  },

  [theme.breakpoints.up("md")]: {
    width: `calc(100% - ${drawerWidth + (sideWidth ? HEADER_SIDE_WIDTH_OPEN : 0)}px)`,
    height: "77px",
    padding: theme.spacing(2.5),
  },

  ...theme.applyStyles("dark", {
    borderBottom: `1px solid ${colors.borderDark}`,
  }),
}));

export const MainContent = styled(SimpleBar, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile: boolean }>(({ theme, isMobile }) => ({
  flexGrow: 1,
  overflowX: "hidden",
  width: "100%",
  marginTop: "68px",
  height: "calc(100vh - 68px)",
  paddingBottom: isMobile ? theme.spacing(7) : 0,

  [theme.breakpoints.up("md")]: {
    marginTop: "77px",
    height: "calc(100vh - 77px)",
  },
}));

export const MobileFooterContainer = styled(Stack)(({ theme }) => ({
  position: "fixed",
  flexDirection: "row",
  alignItems: "stretch",
  bottom: 0,
  left: 0,
  zIndex: 99,
  width: "100%",
  // Frosted in the page's own color, like the header it mirrors.
  background: `rgba(${theme.vars.palette.background.defaultChannel} / 0.86)`,
  backdropFilter: "blur(14px) saturate(1.2)",
  WebkitBackdropFilter: "blur(14px) saturate(1.2)",
  borderTop: `1px solid ${colors.border}`,
  boxShadow: "0 -6px 20px rgba(0, 0, 0, 0.12)",
  // Clear of a phone's home indicator, and of Telegram's own bar inside the Mini App.
  paddingBottom: "max(env(safe-area-inset-bottom, 0px), var(--tg-safe-bottom, 0px))",
  ...theme.applyStyles("dark", {
    borderTop: `1px solid ${colors.borderDark}`,
    boxShadow: "0 -6px 20px rgba(0, 0, 0, 0.4)",
  }),
})) as typeof Stack;

export const SidebarHeader = styled(Stack)(({ theme }) => ({
  position: "relative",
  px: 2,
  gap: 1.5,
  justifyContent: "center",
  flexDirection: "row",
  height: "77px",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.borderDark : colors.border}`,
}));

export const ChatSidebarHeader = styled(Stack)(({ theme }) => ({
  position: "relative",
  width: "100%",
  padding: "2rem 1rem",
  gap: "1rem",
  justifyContent: "center",
  flexDirection: "row",
  height: "77px",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.borderDark : colors.border}`,
}));
